import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { getClubs } from "../../services/api";
import {
  getPointConfigs,
  savePointConfig,
  updatePointConfig,
  deletePointConfig,
  getActionTypes,
  type PointConfig,
  type ActionType,
} from "../../services/points";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";

export default function PointsConfigPage() {
  const { t } = useTranslation();
  const { user, activeRole } = useAuth();

  const [configs, setConfigs] = useState<PointConfig[]>([]);
  const [actionTypes, setActionTypes] = useState<ActionType[]>([]);
  const [clubs, setClubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PointConfig | null>(null);

  const [formData, setFormData] = useState({
    club_id: null as string | null,
    action_type: "",
    points: 0,
  });

  const isPlatformAdmin = activeRole === "platform_admin";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configsData, actionTypesData, clubsData] = await Promise.all([
        getPointConfigs(),
        getActionTypes(),
        isPlatformAdmin ? getClubs() : Promise.resolve([]),
      ]);

      setConfigs(configsData);
      setActionTypes(actionTypesData);
      // getClubs returns [{ club: {...}, role }], extract just the club objects
      const clubsList = clubsData.map((item: any) => item.club);
      setClubs(clubsList);
    } catch (error) {
      console.error("Failed to load points config data:", error);
      alert("Nu s-au putut încărca datele");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingConfig(null);
    setFormData({
      club_id: null,
      action_type: "",
      points: 0,
    });
    setShowModal(true);
  };

  const openEditModal = (config: PointConfig) => {
    setEditingConfig(config);
    setFormData({
      club_id: config.club_id,
      action_type: config.action_type,
      points: config.points,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setFormData({ club_id: null, action_type: "", points: 0 });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.action_type) {
      alert("Te rugăm să selectezi un tip de acțiune");
      return;
    }

    try {
      await savePointConfig({
        club_id: formData.club_id,
        action_type: formData.action_type,
        points: formData.points,
      });

      await loadData();
      closeModal();
    } catch (error: any) {
      console.error("Failed to save point config:", error);
      alert(error.message || "Nu s-a putut salva configurația de puncte");
    }
  };

  const handleToggleActive = async (config: PointConfig) => {
    try {
      await updatePointConfig(config.id, {
        is_active: !config.is_active,
      });
      await loadData();
    } catch (error: any) {
      console.error("Failed to toggle active status:", error);
      alert(error.message || "Nu s-a putut actualiza configurația");
    }
  };

  const handleDelete = async (config: PointConfig) => {
    if (!window.confirm(`Ștergi configurația de puncte pentru "${config.action_type}"?`)) {
      return;
    }

    try {
      await deletePointConfig(config.id);
      await loadData();
    } catch (error: any) {
      console.error("Failed to delete point config:", error);
      alert(error.message || "Nu s-a putut șterge configurația");
    }
  };

  // Group configs by platform defaults vs club-specific
  const platformDefaults = configs.filter((c) => c.club_id === null);
  const clubConfigs = configs.filter((c) => c.club_id !== null);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Conținut și Educație"
          title={t("points.config_title", "Configurare Puncte")}
          subtitle={t("points.config_description", "Configurează valorile punctelor pentru diferite acțiuni din platformă")}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={openCreateModal}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              {t("points.add_config", "Adaugă Configurație")}
            </AdminButton>
          }
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin"></div>
              <p className="text-slate-500">{t("common.loading", "Se încarcă...")}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Statistics Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("points.total_configs", "Total Configurații")}</p>
                    <p className="text-2xl font-bold text-slate-800">{configs.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-50 rounded-xl">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("points.active_configs", "Active")}</p>
                    <p className="text-2xl font-bold text-slate-800">{configs.filter(c => c.is_active).length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("points.club_configs", "Specifice Clubului")}</p>
                    <p className="text-2xl font-bold text-slate-800">{clubConfigs.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Defaults */}
            {isPlatformAdmin && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    {t("points.platform_defaults", "Valori Implicite Platformă")}
                  </h2>
                </div>

                {platformDefaults.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                    <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-slate-500">{t("points.no_platform_defaults", "Nicio valoare implicită de platformă configurată")}</p>
                    <button
                      onClick={openCreateModal}
                      className="mt-4 text-amber-600 hover:text-amber-700 font-medium text-sm"
                    >
                      + {t("points.add_first_config", "Adaugă prima configurație")}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {platformDefaults.map((config) => (
                      <ConfigCard
                        key={config.id}
                        config={config}
                        onEdit={openEditModal}
                        onToggle={handleToggleActive}
                        onDelete={handleDelete}
                        isPlatformAdmin={isPlatformAdmin}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Club-Specific Configurations */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-800">
                  {t("points.club_specific", "Configurații Specifice Clubului")}
                </h2>
              </div>

              {clubConfigs.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
                  <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-slate-500">{t("points.no_club_configs", "Nicio configurație specifică clubului")}</p>
                  <p className="text-slate-400 text-sm mt-1">
                    {t("points.club_override_hint", "Configurațiile specifice clubului au prioritate față de valorile implicite ale platformei")}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {clubConfigs.map((config) => (
                    <ConfigCard
                      key={config.id}
                      config={config}
                      onEdit={openEditModal}
                      onToggle={handleToggleActive}
                      onDelete={handleDelete}
                      isPlatformAdmin={isPlatformAdmin}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editingConfig
                    ? t("points.edit_config", "Editează Configurație")
                    : t("points.create_config", "Creează Configurație")}
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Club Selection (Platform Admin Only) */}
                {isPlatformAdmin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("points.scope", "Domeniu")}
                    </label>
                    <select
                      value={formData.club_id || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          club_id: e.target.value === "" ? null : e.target.value,
                        })
                      }
                      className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                      disabled={!!editingConfig}
                    >
                      <option value="">
                        {t("points.platform_wide", "Valoare implicită platformă")}
                      </option>
                      {clubs.map((club) => (
                        <option key={club.id} value={club.id}>
                          {club.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Action Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("points.action_type", "Tip Acțiune")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.action_type}
                    onChange={(e) =>
                      setFormData({ ...formData, action_type: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    required
                    disabled={!!editingConfig}
                  >
                    <option value="">
                      {t("points.select_action", "Selectează tipul acțiunii")}
                    </option>
                    {actionTypes.map((type) => (
                      <option key={type.code} value={type.code}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Points */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("points.points_value", "Valoare Puncte")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.points}
                    onChange={(e) =>
                      setFormData({ ...formData, points: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    required
                  />
                  {formData.action_type === "quiz_retake_deduction" && (
                    <p className="text-xs text-amber-600 mt-1">
                      {t("points.deduction_hint", "Puncte de scăzut când sportivul reia un quiz")}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors shadow-sm"
                  >
                    {editingConfig ? t("common.save", "Salvează") : t("common.create", "Creează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Config Card Component
function ConfigCard({
  config,
  onEdit,
  onToggle,
  onDelete,
  isPlatformAdmin,
}: {
  config: PointConfig;
  onEdit: (config: PointConfig) => void;
  onToggle: (config: PointConfig) => void;
  onDelete: (config: PointConfig) => void;
  isPlatformAdmin: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border transition-all hover:shadow-md ${
        config.is_active
          ? "border-slate-200 hover:border-amber-300"
          : "border-slate-200 opacity-60"
      }`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="font-semibold text-slate-800 mb-2">
              {config.action_type.split("_").map(word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              ).join(" ")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {config.club && (
                <span className="text-xs px-2.5 py-1 bg-purple-50 text-purple-600 rounded-full font-medium">
                  {config.club.name}
                </span>
              )}
              {!config.club_id && (
                <span className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 rounded-full font-medium">
                  {t("points.platform_default", "Implicit Platformă")}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => onToggle(config)}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              config.is_active
                ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                : "bg-red-50 text-red-500 hover:bg-red-100"
            }`}
          >
            {config.is_active ? t("common.active", "Activ") : t("common.inactive", "Inactiv")}
          </button>
        </div>

        <div className="flex items-end gap-2 mb-4">
          <div className="text-4xl font-bold text-amber-500">{config.points}</div>
          <div className="text-sm text-slate-500 pb-1">
            {t("points.points_label", "puncte")}
          </div>
        </div>

        <div className="flex gap-2 pt-3 border-t border-slate-100">
          <button
            onClick={() => onEdit(config)}
            className="flex-1 px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t("common.edit", "Editează")}
          </button>
          <button
            onClick={() => onDelete(config)}
            className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {t("common.delete", "Șterge")}
          </button>
        </div>
      </div>
    </div>
  );
}
